import React, { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { rolesAPI } from '../../api';
import { useAppStore } from '../../stores/useAppStore';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import UsersTabs from './UsersTabs';
import RoleFormModal from './RoleFormModal';
import { apiError, permissionLabels } from './usersUtils';

const Chip = ({ children, tone = 'gray' }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-sm ${
      tone === 'blue' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'
    }`}
  >
    {children}
  </span>
);

const peopleText = (role) => {
  const n = role.user_count;
  const base = n === 0 ? 'Nobody yet' : n === 1 ? '1 person' : `${n} people`;
  return role.inactive_user_count ? `${base} · ${role.inactive_user_count} switched off` : base;
};

const RoleCard = ({ role, catalogue, onEdit, onDelete }) => {
  const pages = catalogue.filter((p) => p.group === 'page');
  const actions = catalogue.filter((p) => p.group === 'action');
  const pageLabels = permissionLabels(role.permissions.filter((k) => pages.some((p) => p.key === k)), catalogue);
  const actionLabels = permissionLabels(role.permissions.filter((k) => actions.some((p) => p.key === k)), catalogue);
  const holders = role.user_count + role.inactive_user_count;

  return (
    <div className="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {role.name}
            {role.is_system && <span className="ml-2 align-middle text-sm font-normal text-gray-500">Built-in</span>}
          </h2>
          {role.description && <p className="text-sm text-gray-500">{role.description}</p>}
        </div>
        <div className="text-right text-sm text-gray-700 whitespace-nowrap">
          <div>{peopleText(role)}</div>
          <div className="text-gray-500">Discount up to {role.discount_limit}%</div>
        </div>
      </div>

      <div className="mt-4 space-y-3 flex-1">
        {role.is_admin ? (
          <div className="flex flex-wrap gap-1.5">
            <Chip tone="blue">Everything, always</Chip>
            <Chip tone="blue">Users and roles</Chip>
          </div>
        ) : (
          <>
            <div>
              <div className="mb-1 text-sm font-medium text-gray-600">Pages</div>
              <div className="flex flex-wrap gap-1.5">
                {pageLabels.length ? pageLabels.map((l) => <Chip key={l}>{l}</Chip>) : <span className="text-sm text-gray-500">None</span>}
              </div>
            </div>
            {actionLabels.length > 0 && (
              <div>
                <div className="mb-1 text-sm font-medium text-gray-600">Extra</div>
                <div className="flex flex-wrap gap-1.5">
                  {actionLabels.map((l) => (
                    <Chip key={l} tone="blue">
                      {l}
                    </Chip>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="mt-4 flex gap-4 border-t border-gray-100 pt-3">
        {role.is_admin ? (
          <span className="text-sm text-gray-500">The Admin role can&apos;t be changed.</span>
        ) : (
          <button className="text-sm font-medium text-blue-600 hover:text-blue-800" onClick={() => onEdit(role)}>
            Edit ticks
          </button>
        )}
        {!role.is_system &&
          (holders === 0 ? (
            <button className="text-sm font-medium text-red-600 hover:text-red-800" onClick={() => onDelete(role)}>
              Delete
            </button>
          ) : (
            <span className="text-sm text-gray-500">Give its people another role to delete it.</span>
          ))}
      </div>
    </div>
  );
};

/** Roles page: what each role can open and do. */
const RolesPage = () => {
  const { addNotification } = useAppStore();
  const [roles, setRoles] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [editing, setEditing] = useState(undefined); // undefined = closed, null = new role
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await rolesAPI.list();
      setRoles(response.data.roles);
      setCatalogue(response.data.permissions);
    } catch (error) {
      setLoadError(apiError(error, 'Could not load the roles. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaved = (saved, isNew) => {
    setEditing(undefined);
    setRoles((list) => (isNew ? [...list, saved] : list.map((r) => (r.id === saved.id ? saved : r))));
    addNotification({
      type: 'success',
      message: isNew ? `Role "${saved.name}" added. Give it to people on the People tab.` : `Role "${saved.name}" saved.`,
    });
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await rolesAPI.remove(deleting.id);
      setRoles((list) => list.filter((r) => r.id !== deleting.id));
      addNotification({ type: 'success', message: `Role "${deleting.name}" deleted.` });
      setDeleting(null);
    } catch (error) {
      addNotification({ type: 'error', message: apiError(error, 'Could not delete the role.') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-sm text-gray-500">A role is a list of ticks: the pages and extra things its people can use.</p>
        </div>
        <Button onClick={() => setEditing(null)} disabled={loading || !!loadError}>
          <Plus className="h-4 w-4 mr-1" />
          Add role
        </Button>
      </div>

      <UsersTabs />

      {loading ? (
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : loadError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {loadError}{' '}
          <button onClick={load} className="font-medium underline">
            Try again
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {roles.map((role) => (
              <RoleCard key={role.id} role={role} catalogue={catalogue} onEdit={setEditing} onDelete={setDeleting} />
            ))}
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Changes reach people the next time they open or refresh the POS.
          </p>
        </>
      )}

      <RoleFormModal
        isOpen={editing !== undefined}
        onClose={() => setEditing(undefined)}
        role={editing || null}
        catalogue={catalogue}
        onSaved={handleSaved}
      />

      <Modal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        title={deleting ? `Delete the role "${deleting.name}"?` : ''}
        size="sm"
      >
        {deleting && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">Nobody has this role. You can make it again later if you need it.</p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setDeleting(null)} autoFocus>
                Cancel
              </Button>
              <Button variant="danger" onClick={confirmDelete} disabled={busy}>
                Delete role
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RolesPage;
